/**
 * router/AuthorizedRoute.tsx
 * desc: authorized router component
 * note: this is a custom utility function that adds protected routes (based on "react-router-config", which currently doesn't offer that feature)
 */

import { isRouteAuthorized } from './utilities';
import { translate } from '../utilities/localization';
import { trackPage } from '../utilities/analytics';
import Error403 from '../modules/Error403';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router';
import type { AppRoute, RouteExtraProps } from './types';

interface AuthorizedRouteProps {
    route: AppRoute;
    extraProps: RouteExtraProps;
}

let prevPath: string | null = null;

const AuthorizedRoute = ({ route, extraProps }: AuthorizedRouteProps) => {
    const location = useLocation();
    let result = null;
    // handle redirect prop
    if (route.redirect && route.redirect !== route.path) {
        return <Navigate to={ route.redirect } replace />;
    }

    // if protected route but no token exists, then redirect to login
    // note: any truthy value is acceptable for our purposes here
    //       api endpoints return 401 if a token is invalid
    //       and the request utility listens for 401 errors

    const isAuthorized = isRouteAuthorized(route, extraProps.user?.permissions || []);

    // update page title
    if (route.title) document.title = translate(route.title);
    else document.title = translate('common.document.title');

    // track page when route changes
    trackPage();

    if (isAuthorized) {
        const Page = route.component!;
        result =  <Page
            key={ location.pathname }
            { ...extraProps }
            location={ location }
            navigate={ useNavigate() }
            routeParams={ useParams() }
            prevPath={ prevPath }
        />;
        prevPath = window.location.pathname;
    } else {
        result = <Error403 />;
    }

    return result;
};


export default AuthorizedRoute;
